import { useEffect, useState } from "react";

/**
 * Whether an element has scrolled into view yet, for every scroll reveal on the site (design notes
 * salon-home-landing.md 9.2 and salon-booking-premium-redesign.md 5.4). Returns a callback ref for the element and whether it is shown; once
 * shown it stays shown. Without IntersectionObserver (jsdom, very old browsers) everything is shown at
 * once, so nothing is ever hidden for good.
 */
export function useReveal<T extends Element>(): [(element: T | null) => void, boolean] {
  const [element, setElement] = useState<T | null>(null);
  const [shown, setShown] = useState(() => typeof IntersectionObserver !== "function");

  useEffect(() => {
    if (!element || shown) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, shown]);

  return [setElement, shown];
}
